import axios from 'axios';
import type { TrajectoryData } from '@/types/trace';
import { configureAuthInterceptors } from './authConfig';

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Configure auth interceptors (credentials, group header, 401 handling)
configureAuthInterceptors(api);

export interface TrajectoryListItem {
  id: string;
  created_at: string;
  status: string;
  report_title?: string;
  total_events: number;
  agent_name?: string;
}

export interface TrajectoryListResponse {
  status: string;
  trajectories: TrajectoryListItem[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

export interface TrajectoryDetailResponse {
  status: string;
  data: TrajectoryData;
}

/**
 * Fetch list of trajectories with pagination and filters
 */
export async function fetchTrajectories(params: {
  page?: number;
  per_page?: number;
  agent_name?: string;
  status?: string;
  start_date?: string;
  end_date?: string;
}): Promise<TrajectoryListResponse> {
  const response = await api.get<TrajectoryListResponse>('/trajectories', { params });
  return response.data;
}

/**
 * Fetch single trajectory details
 */
export async function fetchTrajectory(id: string): Promise<TrajectoryData> {
  const response = await api.get<TrajectoryDetailResponse>(`/trajectories/${id}`);
  return response.data.data;
}

/**
 * Fetch trajectory metadata (debug endpoint)
 */
export async function fetchTrajectoryMetadata(id: string) {
  const response = await api.get(`/_trajectories`, {
    params: { trajectory_id: id }
  });
  return response.data;
}

/**
 * Fetch trajectory events (debug endpoint)
 */
export async function fetchTrajectoryEvents(id: string) {
  const response = await api.get(`/_events`, {
    params: { trajectory_id: id }
  });
  return response.data;
}

export default api;
